import {
  Box,
  createStyles,
  FormControlLabel,
  Grid,
  Input,
  makeStyles,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useRef } from 'react';
import { UrlMatchTypeGq } from '../../../../../graphql/projects/models/UrlMatchTypeGq';
import TaskUrl from '../../Tables/ContentTaskTable/TaskUrl';
import { UrlInputBag } from './useUrlInput';

export interface UrlInputProps {
  placeholder: string;
  updating: boolean;
  url: string;
  setUrl: (url: string) => void;
  urlMatchType: UrlMatchTypeGq;
  setUrlMatchType: (urlMatchType: string) => void;
  urlInputBag: UrlInputBag;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    smallTitle: {
      fontSize: '18px',
    },
    boldText: {
      fontWeight: 500,
    },
    radioLabelSelected: {
      ...theme.typography.subtitle2,
      color: theme.palette.text.primary,
    },
    radioLabel: {
      ...theme.typography.subtitle2,
      color: theme.palette.text.secondary,
    },
    urlInputContainer: {
      marginLeft: theme.spacing(0.5),
      marginTop: -theme.spacing(0.5),
      backgroundColor: '#fbfbfb',
      borderRadius: '4px',
      position: 'relative',
    },
    urlInputField: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      borderRadius: '4px',
      '&:focus': {
        outline: '1px solid #aaa',
      },
    },
    urlInputFieldFont: {
      ...theme.typography.body2,
    },
    inputFieldUnderline: {
      '&::before': {
        borderBottomWidth: '0px !important',
      },
      '&::after': {
        borderBottomWidth: '0px !important',
      },
      '&:hover': {
        '&:not(Mui-disabled)': {
          '&:before': {
            borderBottomWidth: '0px !important',
            borderBottomColor: theme.palette.primary.main,
          },
        },
      },
    },
    urlSearchList: {
      zIndex: theme.zIndex.modal + 1,
      position: 'absolute',
      backgroundColor: '#fff',
      with: '100%',
      top: '36px',
      left: '0px',
      right: '0px',
      borderRadius: '4px',
      boxShadow: theme.shadows[2],
    },
    tableRow: {
      cursor: 'pointer',
    },
  })
);

export const UrlInput: React.FC<UrlInputProps> = ({
  placeholder,
  updating,
  urlMatchType,
  setUrlMatchType,
  url,
  setUrl,
  urlInputBag,
}) => {
  const {
    page,
    setPage,
    currentStateUrlsQuery,
    showSearchList,
    setShowSearchList,
    handleSelectUrl,
    cleanedClientUrl,
    relevantCurrentUrls,
    clientUrl,
  } = urlInputBag;

  // Click outside form field
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    const clickHandler = (event: any) => {
      // Check if event target element is inside form
      if (formRef.current && !formRef.current?.contains(event.target)) {
        setShowSearchList(false);
      }
    };

    // Event listener add/cleanup
    document.addEventListener('click', clickHandler, true);
    return () => document.removeEventListener('click', clickHandler, true);
  }, []);

  // Styling
  const classes = useClasses();

  return (
    <Grid container spacing={1} item xs={12}>
      <Grid item container spacing={3} alignItems={'center'}>
        <Grid item>
          <Typography variant={'h6'} color={'secondary'} className={classes.smallTitle}>
            Url
          </Typography>
        </Grid>
        <Grid item xs>
          <RadioGroup
            value={urlMatchType}
            onChange={(event) => setUrlMatchType(event.target.value)}
          >
            <Grid container>
              <Grid item>
                <FormControlLabel
                  onSubmit={(event) => event.preventDefault()}
                  value={UrlMatchTypeGq.CONTAINS}
                  control={<Radio size={'small'} color={'primary'} />}
                  label="Contains"
                  classes={{
                    label:
                      urlMatchType === UrlMatchTypeGq.CONTAINS
                        ? classes.radioLabelSelected
                        : classes.radioLabel,
                  }}
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  onSubmit={(event) => event.preventDefault()}
                  value={UrlMatchTypeGq.STARTS}
                  control={<Radio size={'small'} color={'primary'} />}
                  label="Starting with"
                  classes={{
                    label:
                      urlMatchType === UrlMatchTypeGq.STARTS
                        ? classes.radioLabelSelected
                        : classes.radioLabel,
                  }}
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  onSubmit={(event) => event.preventDefault()}
                  value={UrlMatchTypeGq.EXACT}
                  control={<Radio size={'small'} color={'primary'} />}
                  label="Exact match"
                  classes={{
                    label:
                      urlMatchType === UrlMatchTypeGq.EXACT
                        ? classes.radioLabelSelected
                        : classes.radioLabel,
                  }}
                />
              </Grid>
            </Grid>
          </RadioGroup>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container justifyContent={'flex-end'}>
          <Grid container wrap={'nowrap'} alignItems={'center'}>
            <Grid item>
              <Typography
                variant={'subtitle2'}
                className={classes.boldText}
                color={'secondary'}
                gutterBottom
              >
                {cleanedClientUrl}
              </Typography>
            </Grid>
            <Grid item xs>
              <Box className={classes.urlInputContainer}>
                <form
                  ref={formRef}
                  onSubmit={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <Input
                    fullWidth
                    value={url}
                    onChange={(event) => setUrl(event.target.value)}
                    placeholder={placeholder}
                    classes={{
                      root: classes.urlInputFieldFont,
                      input: classes.urlInputField,
                      underline: classes.inputFieldUnderline,
                    }}
                    onFocus={() => setShowSearchList(true)}
                  />
                  {currentStateUrlsQuery?.count === 0 && showSearchList && (
                    <Box p={2} fontStyle="oblique" className={classes.urlSearchList}>
                      <Typography variant={'body2'}>No urls found</Typography>
                    </Box>
                  )}
                  {currentStateUrlsQuery?.count >= 1 && showSearchList && (
                    <Box className={classes.urlSearchList}>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {currentStateUrlsQuery.pagedUrls?.map((pagedUrl) => (
                              <TableRow
                                key={pagedUrl.contentTaskId}
                                hover={!updating}
                                className={classes.tableRow}
                                onClick={(event) => handleSelectUrl(event, pagedUrl.url)}
                              >
                                <TableCell>
                                  <TaskUrl contentTaskUrl={pagedUrl.url} clientUrl={clientUrl} />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <TablePagination
                          component={'div'}
                          count={currentStateUrlsQuery.count ?? 0}
                          page={currentStateUrlsQuery.page ?? 0}
                          rowsPerPage={currentStateUrlsQuery.rowsPerPage}
                          rowsPerPageOptions={[]}
                          onPageChange={(event, page) => setPage(page)}
                          onRowsPerPageChange={(event) => {}}
                        />
                      </TableContainer>
                    </Box>
                  )}
                </form>
              </Box>
            </Grid>
          </Grid>
          <Grid item>
            <Typography variant="caption" align="right" color={'textSecondary'}>
              {relevantCurrentUrls?.length > 0 ? relevantCurrentUrls?.length : 0} urls
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
